import { Nav, NavDropdown } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Version from '../../version'
import {
	BookUser, Building, Building2, CreditCard, Database,
	FilePenLine, FileStack, LayoutDashboard, ReceiptEuro,
	Settings2, Tag, Users,
} from 'lucide-react'
import { useAuth } from '../auth/useAuth'

type SidebarClientProps = {
	rol?: string
	isAdmin?: boolean
	logo?: string
}

export default function SidebarClient({
	rol,
	isAdmin = false,
	logo = '/images/logotipo-transparente.png',
}: SidebarClientProps) {
	const { t } = useTranslation()
	const { pathname } = useLocation()
	const { logoutClient } = useAuth()

	return (
		// d-none d-lg-block: oculto en xs/sm/md, visible en lg+
		<div className="sidebar d-none d-lg-flex flex-column" id="sidebar">
			<div className="text-center my-2">
				<img id="logo" className="mx-auto" src={logo} alt="logo" />
			</div>

			<div className="text-white opacity-50 pt-5">{t('menus.menu')}</div>
			<hr className="separador opacity-50" />

			<Nav as="ul" className="flex-column p-0 main-nav">
				{(rol === 'admin') && (
					<>
						<Nav.Item as="li" className={pathname.includes(`${t('url.admin')}/${t('url.dashboard')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.admin')}/${t('url.dashboard')}`}>
								<LayoutDashboard size={16} className="me-2" /> {t('Dashboard')}
							</Link>
						</Nav.Item>
						<Nav.Item as="li" className={pathname.includes(`${t('url.admin')}/${t('url.documentsManagement')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.admin')}/${t('url.documentsManagement')}`}>
								<FileStack size={16} className="me-2" /> {t('Documents')}
							</Link>
						</Nav.Item>
						<NavDropdown
							title={<><Database size={16} className="me-3" />{t('bbdd')}</>}
							id="bbdd-nav-dropdown"
							className={
								[
									`/${t('url.admin')}/${t('url.users')}`,
									`/${t('url.admin')}/${t('url.sucursalsManagement')}`,
									`/${t('url.admin')}/${t('url.companiesManagement')}`,
									`/${t('url.admin')}/${t('url.categoriesManagement')}`,
									`/${t('url.admin')}/${t('url.sinestersTypesManagement')}`,
									`/${t('url.admin')}/${t('url.involvedTypesManagement')}`,
								].some(p => pathname.includes(p)) ? 'active' : ''
							}
						>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.users')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.users')}`)}>
								<Users size={16} className="me-2" /> {t('Users')}
							</NavDropdown.Item>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.sucursalsManagement')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.sucursalsManagement')}`)}>
								<Building size={16} className="me-2" /> {t('Sucursals')}
							</NavDropdown.Item>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.companiesManagement')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.companiesManagement')}`)}>
								<Building2 size={16} className="me-2" /> {t('Companies')}
							</NavDropdown.Item>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.categoriesManagement')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.categoriesManagement')}`)}>
								<Tag size={16} className="me-2" /> {t('Categories')}
							</NavDropdown.Item>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.sinestersTypesManagement')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.sinestersTypesManagement')}`)}>
								<img src="/images/sinester-white.svg" alt="" width={16} height={16} /> {t('sinestersTypesManagement')}
							</NavDropdown.Item>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.paymentMethodsManagement')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.paymentMethodsManagement')}`)}>
								<CreditCard size={16} className="me-2" /> {t('paymentMethodsManagement')}
							</NavDropdown.Item>
							<NavDropdown.Item as={Link} to={`/${t('url.admin')}/${t('url.involvedTypesManagement')}`} active={pathname.includes(`/${t('url.admin')}/${t('url.involvedTypesManagement')}`)}>
								<CreditCard size={16} className="me-2" /> {t('involvedTypesManagement')}
							</NavDropdown.Item>
						</NavDropdown>
					</>
				)}

				{(rol === 'clerk') && (
					<>
						<Nav.Item as="li" className={pathname.includes(`${t('url.clerk')}/${t('url.dashboard')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.clerk')}/${t('url.dashboard')}`}>
								<LayoutDashboard size={16} className="me-2" /> {t('Dashboard')}
							</Link>
						</Nav.Item>
						<Nav.Item as="li" className={pathname.includes(`${t('url.clerk')}/${t('url.documents')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.clerk')}/${t('url.documents')}`}>
								<FileStack size={16} className="me-2" /> {t('Documents')}
							</Link>
						</Nav.Item>
					</>
				)}

				{(rol === 'commercial') && (
					<>
						<Nav.Item as="li" className={pathname.includes(`${t('url.commercial')}/${t('url.dashboard')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.commercial')}/${t('url.dashboard')}`}>
								<LayoutDashboard size={16} className="me-2" /> {t('Dashboard')}
							</Link>
						</Nav.Item>
						<Nav.Item as="li" className={pathname.includes(`${t('url.commercial')}/${t('url.documents')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.commercial')}/${t('url.documents')}`}>
								<FileStack size={16} className="me-2" /> {t('Documents')}
							</Link>
						</Nav.Item>
					</>
				)}

				{/* Todos los roles */}
				<Nav.Item as="li" className={pathname.includes(`${t('url.' + rol)}/${t('url.clients')}`) ? 'active' : ''}>
					<Link className="nav-link-pill" to={`/${t('url.' + rol)}/${t('url.clients')}`}>
						<BookUser size={16} className="me-2" /> {t('Clients')}
					</Link>
				</Nav.Item>
				<Nav.Item as="li" className={pathname.includes(`${t('url.' + rol)}/${t('url.policies')}`) ? 'active' : ''}>
					<Link className="nav-link-pill" to={`/${t('url.' + rol)}/${t('url.policies')}`}>
						<FilePenLine size={16} className="me-2" /> {t('Policies')}
					</Link>
				</Nav.Item>
				<Nav.Item as="li" className={pathname.includes(`${t('url.' + rol)}/${t('url.sinesters')}`) ? 'active' : ''}>
					<Link className="nav-link-pill" to={`/${t('url.' + rol)}/${t('url.sinesters')}`}>
						<img src="/images/sinester-white.svg" alt="" className="me-2" width={16} /> {t('Sinesters')}
					</Link>
				</Nav.Item>
				<Nav.Item as="li" className={pathname.includes(`${t('url.' + rol)}/${t('url.receipts')}`) ? 'active' : ''}>
					<Link className="nav-link-pill" to={`/${t('url.' + rol)}/${t('url.receipts')}`}>
						<ReceiptEuro size={16} className="me-2" /> {t('Receipts')}
					</Link>
				</Nav.Item>

				{(rol === 'clerk' && isAdmin) && (
					<>
						<Nav.Item as="li" className={pathname.includes(`${t('url.clerk')}/${t('url.reports')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.clerk')}/${t('url.reports')}`}>
								<FileStack size={16} className="me-2" /> {t('Reports')}
							</Link>
						</Nav.Item>
						<Nav.Item as="li" className={pathname.includes(`${t('url.clerk')}/${t('url.settings')}`) ? 'active' : ''}>
							<Link className="nav-link-pill" to={`/${t('url.clerk')}/${t('url.settings')}`}>
								<Settings2 size={16} className="me-2" /> {t('Settings')}
							</Link>
						</Nav.Item>
					</>
				)}

				<Nav.Item as="li" onClick={logoutClient}>
					<Link className="nav-link-pill" to="/">
						<img src="/images/leave.svg" alt="" className="me-2" width={16} /> {t('menus.logout')}
					</Link>
				</Nav.Item>
			</Nav>

			<div className="text-center opacity-50">
				<Version />
			</div>
		</div>
	)
}
